import { useState, useEffect } from 'react';
import { ChevronLeft, Edit, Container, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SignedLink } from '../common/SignedFile';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { ConfirmModal } from '../common/ConfirmModal';

interface TrailerDetailPageProps {
  trailerId: string;
}

export function TrailerDetailPage({ trailerId }: TrailerDetailPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [trailer, setTrailer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadTrailerData();
  }, [trailerId]);

  async function loadTrailerData() {
    try {
      const { data: trailerData, error: trailerError } = await supabase
        .from('trailers')
        .select('*')
        .eq('id', trailerId)
        .single();

      if (trailerError) throw trailerError;
      setTrailer(trailerData);
    } catch (error) {
      console.error('Error loading trailer:', error);
      toast.error('Failed to load trailer data');
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteTrailer = async () => {
    try {
      const { error } = await supabase
        .from('trailers')
        .delete()
        .eq('id', trailerId);

      if (error) throw error;

      toast.success('Trailer deleted successfully!');
      setShowDeleteConfirm(false);
      navigate('/fleet');
    } catch (error) {
      console.error('Error deleting trailer:', error);
      toast.error('Failed to delete trailer');
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!trailer) {
    return (
      <div className="text-center py-12">
        <Container className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Trailer Not Found</h2>
        <button
          onClick={() => navigate('/fleet')}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Back to Fleet
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/fleet')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">
                  {trailer.unit_number || trailer.trailer_id}
                </h1>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  trailer.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {trailer.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                {trailer.year && <span>{trailer.year} {trailer.make} {trailer.model}</span>}
                {trailer.trailer_type && <span className="capitalize">{trailer.trailer_type.replace('_', ' ')}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/trailers/edit/${trailerId}`)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Trailer
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Trailer
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Unit Number</label>
                <p className="font-medium text-slate-900">{trailer.unit_number || '-'}</p>
              </div>
              {trailer.vin && (
                <div>
                  <label className="text-sm text-slate-600">VIN</label>
                  <p className="font-medium text-slate-900 font-mono text-sm">{trailer.vin}</p>
                </div>
              )}
              {trailer.make && (
                <div>
                  <label className="text-sm text-slate-600">Make</label>
                  <p className="font-medium text-slate-900">{trailer.make}</p>
                </div>
              )}
              {trailer.model && (
                <div>
                  <label className="text-sm text-slate-600">Model</label>
                  <p className="font-medium text-slate-900">{trailer.model}</p>
                </div>
              )}
              {trailer.year && (
                <div>
                  <label className="text-sm text-slate-600">Year</label>
                  <p className="font-medium text-slate-900">{trailer.year}</p>
                </div>
              )}
              {trailer.trailer_type && (
                <div>
                  <label className="text-sm text-slate-600">Type</label>
                  <p className="font-medium text-slate-900 capitalize">{trailer.trailer_type.replace('_', ' ')}</p>
                </div>
              )}
              {trailer.license_plate && (
                <div>
                  <label className="text-sm text-slate-600">License Plate</label>
                  <p className="font-medium text-slate-900">{trailer.license_plate}</p>
                </div>
              )}
              {trailer.license_state && (
                <div>
                  <label className="text-sm text-slate-600">License State</label>
                  <p className="font-medium text-slate-900">{trailer.license_state}</p>
                </div>
              )}
              {trailer.length_feet && (
                <div>
                  <label className="text-sm text-slate-600">Length</label>
                  <p className="font-medium text-slate-900">{trailer.length_feet} ft</p>
                </div>
              )}
              {trailer.capacity_weight && (
                <div>
                  <label className="text-sm text-slate-600">Capacity</label>
                  <p className="font-medium text-slate-900">{trailer.capacity_weight} lbs</p>
                </div>
              )}
              {trailer.serial_number && (
                <div>
                  <label className="text-sm text-slate-600">Serial Number</label>
                  <p className="font-medium text-slate-900">{trailer.serial_number}</p>
                </div>
              )}
              {trailer.tire_size && (
                <div>
                  <label className="text-sm text-slate-600">Tire Size</label>
                  <p className="font-medium text-slate-900">{trailer.tire_size}</p>
                </div>
              )}
              {trailer.width_feet && (
                <div>
                  <label className="text-sm text-slate-600">Width</label>
                  <p className="font-medium text-slate-900">{trailer.width_feet} ft</p>
                </div>
              )}
              {trailer.height_feet && (
                <div>
                  <label className="text-sm text-slate-600">Height</label>
                  <p className="font-medium text-slate-900">{trailer.height_feet} ft</p>
                </div>
              )}
              {trailer.weight_lbs && (
                <div>
                  <label className="text-sm text-slate-600">Weight</label>
                  <p className="font-medium text-slate-900">{trailer.weight_lbs} lbs</p>
                </div>
              )}
            </div>
          </div>

          {/* Tag Information */}
          {(trailer.tag_number || trailer.tag_state) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Tag Information</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.tag_number && (
                  <div>
                    <label className="text-sm text-slate-600">Tag Number</label>
                    <p className="font-medium text-slate-900">{trailer.tag_number}</p>
                  </div>
                )}
                {trailer.tag_state && (
                  <div>
                    <label className="text-sm text-slate-600">Tag State</label>
                    <p className="font-medium text-slate-900">{trailer.tag_state}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DOT Information */}
          {(trailer.dot_issued_date || trailer.dot_state || trailer.dot_expiration_date) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Annual DOT</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.dot_issued_date && (
                  <div>
                    <label className="text-sm text-slate-600">Issued Date</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.dot_issued_date).toLocaleDateString()}</p>
                  </div>
                )}
                {trailer.dot_state && (
                  <div>
                    <label className="text-sm text-slate-600">State</label>
                    <p className="font-medium text-slate-900">{trailer.dot_state}</p>
                  </div>
                )}
                {trailer.dot_expiration_date && (
                  <div>
                    <label className="text-sm text-slate-600">Expiration Date</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.dot_expiration_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Insurance Information */}
          {(trailer.insurance_type || trailer.insurance_carrier || trailer.insurance_expiration_date) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Insurance</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.insurance_type && (
                  <div>
                    <label className="text-sm text-slate-600">Insurance Type</label>
                    <p className="font-medium text-slate-900 capitalize">{trailer.insurance_type}</p>
                  </div>
                )}
                {trailer.insurance_carrier && (
                  <div>
                    <label className="text-sm text-slate-600">Insurance Carrier</label>
                    <p className="font-medium text-slate-900">{trailer.insurance_carrier}</p>
                  </div>
                )}
                {trailer.insurance_expiration_date && (
                  <div>
                    <label className="text-sm text-slate-600">Insurance Expiration Date</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.insurance_expiration_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Assignment & Status */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Assignment & Status</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Driver Assigned</label>
                <p className="font-medium text-slate-900">{trailer.driver_assigned || 'Not Assigned'}</p>
              </div>
              <div>
                <label className="text-sm text-slate-600">Owner</label>
                <p className="font-medium text-slate-900">{trailer.owner || '-'}</p>
              </div>
              <div>
                <label className="text-sm text-slate-600">GPS</label>
                <p className="font-medium text-slate-900">{trailer.gps || 'NO'}</p>
              </div>
              <div>
                <label className="text-sm text-slate-600">Status</label>
                <p className="font-medium text-slate-900">{trailer.status || 'ACTIVE'}</p>
              </div>
            </div>
          </div>

          {/* Expiration Dates */}
          {(trailer.registration_expiration || trailer.inspection_expiration) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Expiration Dates</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.registration_expiration && (
                  <div>
                    <label className="text-sm text-slate-600">Registration Expiration</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.registration_expiration).toLocaleDateString()}</p>
                  </div>
                )}
                {trailer.inspection_expiration && (
                  <div>
                    <label className="text-sm text-slate-600">Inspection Expiration</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.inspection_expiration).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {trailer.notes && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Notes</h2>
              <p className="text-slate-700 whitespace-pre-wrap">{trailer.notes}</p>
            </div>
          )}

          {/* Dimensions */}
          {(trailer.length || trailer.width || trailer.height || trailer.weight) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Dimensions</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.length && (
                  <div>
                    <label className="text-sm text-slate-600">Length</label>
                    <p className="font-medium text-slate-900">{trailer.length} ft</p>
                  </div>
                )}
                {trailer.width && (
                  <div>
                    <label className="text-sm text-slate-600">Width</label>
                    <p className="font-medium text-slate-900">{trailer.width} ft</p>
                  </div>
                )}
                {trailer.height && (
                  <div>
                    <label className="text-sm text-slate-600">Height</label>
                    <p className="font-medium text-slate-900">{trailer.height} ft</p>
                  </div>
                )}
                {trailer.weight && (
                  <div>
                    <label className="text-sm text-slate-600">Weight</label>
                    <p className="font-medium text-slate-900">{trailer.weight} lbs</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tag Information */}
          {(trailer.tag_number || trailer.tag_state) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Tag Information</h2>
              <div className="grid grid-cols-2 gap-4">
                {trailer.tag_number && (
                  <div>
                    <label className="text-sm text-slate-600">Tag Number</label>
                    <p className="font-medium text-slate-900">{trailer.tag_number}</p>
                  </div>
                )}
                {trailer.tag_state && (
                  <div>
                    <label className="text-sm text-slate-600">State</label>
                    <p className="font-medium text-slate-900">{trailer.tag_state}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Insurance */}
          {trailer.insurance_type && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Insurance</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Type</label>
                  <p className="font-medium text-slate-900 capitalize">{trailer.insurance_type}</p>
                </div>
                {trailer.insurance_carrier && (
                  <div>
                    <label className="text-sm text-slate-600">Carrier</label>
                    <p className="font-medium text-slate-900">{trailer.insurance_carrier}</p>
                  </div>
                )}
                {trailer.insurance_expiration_date && (
                  <div>
                    <label className="text-sm text-slate-600">Expiration</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.insurance_expiration_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Documents</h2>
            <div className="space-y-3">
              {/* Registration File */}
              <div>
                <label className="text-sm font-medium text-slate-600">Registration File</label>
                {trailer.registration_file_url ? (
                  <div className="mt-1">
                    <SignedLink
                      stored={trailer.registration_file_url}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm w-fit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Registration
                    </SignedLink>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-1">No file uploaded</p>
                )}
              </div>

              {/* DOT File */}
              <div>
                <label className="text-sm font-medium text-slate-600">DOT File</label>
                {trailer.dot_file_url ? (
                  <div className="mt-1">
                    <SignedLink
                      stored={trailer.dot_file_url}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm w-fit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download DOT File
                    </SignedLink>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-1">No file uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - DOT Info */}
        <div className="space-y-6">
          {/* DOT Information */}
          {(trailer.dot_issued_date || trailer.dot_expiration_date) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Annual DOT</h2>
              <div className="space-y-3">
                {trailer.dot_issued_date && (
                  <div>
                    <label className="text-sm text-slate-600">Issued Date</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.dot_issued_date).toLocaleDateString()}</p>
                  </div>
                )}
                {trailer.dot_expiration_date && (
                  <div>
                    <label className="text-sm text-slate-600">Expiration</label>
                    <p className="font-medium text-slate-900">{new Date(trailer.dot_expiration_date).toLocaleDateString()}</p>
                  </div>
                )}
                {trailer.dot_state && (
                  <div>
                    <label className="text-sm text-slate-600">State</label>
                    <p className="font-medium text-slate-900">{trailer.dot_state}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Trailer Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Trailer"
        message={`Are you sure you want to delete ${trailer.unit_number || trailer.trailer_id}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteTrailer}
        onCancel={() => setShowDeleteConfirm(false)}
        type="danger"
      />
    </div>
  );
}
